import { useState, useEffect } from "react";
import { Loader2, FileEdit, Landmark, Pencil } from "lucide-react";
import EcritureEditModal from "../EcritureEditModal";
import { fetchEcrituresForTransaction, type EcritureComptable, type TransactionComptableContext } from "../../../lib/comptable";
import { resolveTransactionComptableContext } from "../../../lib/transactions";
import { formatMontantFCFA } from "@/lib/formatMontant";

const PLACEHOLDER = [
  { c: "6xx", l: "Charge" },
  { c: "521", l: "Trésorerie" },
];
const fmt = (n: number) => formatMontantFCFA(Number(n) || 0);
const fmtDate = (d: string) => (d ? new Date(d).toLocaleDateString("fr-FR") : "—");

export interface TransactionEcrituresTabProps {
  transactionId: string;
  txType: "entree" | "sortie";
}

export default function TransactionEcrituresTab({ transactionId, txType }: TransactionEcrituresTabProps) {
  const [context, setContext] = useState<TransactionComptableContext | null>(null);
  const [ecritures, setEcritures] = useState<EcritureComptable[] | "loading">("loading");
  const [editingEcriture, setEditingEcriture] = useState<EcritureComptable | null>(null);
  const [ecrituresRefreshKey, setEcrituresRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setEcritures("loading");
      const ctx = await resolveTransactionComptableContext(transactionId, txType);
      if (cancelled) return;
      setContext(ctx);
      const { ecritures: list } = await fetchEcrituresForTransaction(transactionId, ctx);
      if (cancelled) return;
      setEcritures(list);
    })();
    return () => { cancelled = true; };
  }, [transactionId, txType, ecrituresRefreshKey]);

  if (ecritures === "loading" || !context) {
    return (
      <div style={{ padding: 32, textAlign: "center", color: "#9A8878" }}>
        <Loader2 size={24} className="animate-spin" style={{ animation: "spin 1s linear infinite" }} />
      </div>
    );
  }

  if (ecritures.length === 0) {
    const emptyMsg =
      context === "direct"
        ? "Aucune écriture générée. La transaction doit être justifiée (trésorerie directe ou rapprochement) pour produire des écritures."
        : context === "achat"
          ? "Aucune écriture de paiement. Le rapprochement facture achat génère D4011/C521."
          : context === "vente_encaissement"
            ? "Aucune écriture d'encaissement. Le rapprochement facture/avoir génère D521/C411."
            : "Aucune écriture de remboursement. Le rapprochement avoir (sortie) génère D411/C521.";
    return <div style={{ padding: 12, fontSize: 13, color: "#64748b" }}>{emptyMsg}</div>;
  }

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {ecritures.map((ec) => {
          const displayedJournal =
            ec.source_type === "transaction" && ec.journal_code?.toUpperCase().startsWith("OD")
              ? "BQ1"
              : ec.journal_code || "—";
          return (
          <section key={ec.id} style={{ border: "1px solid #e5e7eb", borderRadius: 10, background: "#fff", overflow: "hidden" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 10px",
                borderBottom: "1px solid #f3f4f6",
                background: "#fff",
                fontSize: 12,
                color: "#374151",
                flexWrap: "wrap",
              }}
            >
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 6px", borderRadius: 999, border: "1px solid #e5e7eb", fontSize: 11, fontWeight: 700, color: "#374151" }}>
                <Landmark size={12} />
                {displayedJournal}
              </span>
              <span style={{ color: "#6b7280", fontSize: 12 }}>{fmtDate(ec.date_ecriture)}</span>
              <span style={{ color: "#9ca3af" }}>•</span>
              <span style={{ color: "#6b7280", fontSize: 12 }}>Pièce {ec.date_piece ? fmtDate(ec.date_piece) : "—"}</span>
              <span style={{ color: "#9ca3af" }}>•</span>
              <span style={{ color: "#111827", fontWeight: 500, fontSize: 12, flex: 1, minWidth: 160, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {ec.libelle}
              </span>
              {ec.statut === "draft" && (
                <button
                  type="button"
                  onClick={() => setEditingEcriture(ec)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    padding: "4px 8px",
                    border: "1px solid #d1d5db",
                    borderRadius: 8,
                    background: "#fff",
                    color: "#374151",
                    fontSize: 11,
                    cursor: "pointer",
                  }}
                >
                  <FileEdit size={12} /> Modifier
                </button>
              )}
            </div>

            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #f3f4f6" }}>
                  <th style={{ textAlign: "left", padding: "6px 10px", fontSize: 11, color: "#9ca3af", fontWeight: 600 }}>COMPTE</th>
                  <th style={{ textAlign: "left", padding: "6px 10px", fontSize: 11, color: "#9ca3af", fontWeight: 600 }}>LIBELLÉ</th>
                  <th style={{ textAlign: "right", padding: "6px 10px", fontSize: 11, color: "#9ca3af", fontWeight: 600, width: 140 }}>DÉBIT</th>
                  <th style={{ textAlign: "right", padding: "6px 10px", fontSize: 11, color: "#9ca3af", fontWeight: 600, width: 140 }}>CRÉDIT</th>
                  <th style={{ textAlign: "center", padding: "6px 10px", fontSize: 11, color: "#9ca3af", fontWeight: 600, width: 52 }}> </th>
                </tr>
              </thead>
              <tbody>
                {(ec.lignes || []).map((l) => (
                  <tr key={l.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                    <td style={{ padding: "4px 10px", fontSize: 13, color: "#2563eb", cursor: "pointer" }}>{l.compte_numero}</td>
                    <td style={{ padding: "4px 10px", fontSize: 13, color: "#374151" }}>{l.compte_libelle}</td>
                    <td style={{ padding: "4px 10px", textAlign: "right", fontSize: 13, color: "#111827", fontVariantNumeric: "tabular-nums" }}>
                      {Number(l.debit) > 0 ? fmt(Number(l.debit)) : "—"}
                    </td>
                    <td style={{ padding: "4px 10px", textAlign: "right", fontSize: 13, color: "#111827", fontVariantNumeric: "tabular-nums" }}>
                      {Number(l.credit) > 0 ? fmt(Number(l.credit)) : "—"}
                    </td>
                    <td style={{ padding: "4px 10px", textAlign: "center" }}>
                      <button type="button" style={{ border: "none", background: "transparent", color: "#9ca3af", cursor: "pointer", padding: 0 }}>
                        <Pencil size={12} />
                      </button>
                    </td>
                  </tr>
                ))}
                <tr style={{ borderTop: "2px solid #e5e7eb", background: "#fcfcfd" }}>
                  <td colSpan={2} style={{ padding: "6px 10px", fontSize: 13, fontWeight: 600, color: "#111827" }}>Total</td>
                  <td style={{ padding: "6px 10px", textAlign: "right", fontSize: 13, fontWeight: 600, color: "#111827", fontVariantNumeric: "tabular-nums" }}>
                    {fmt((ec.lignes || []).reduce((s, l) => s + (Number(l.debit) || 0), 0))}
                  </td>
                  <td style={{ padding: "6px 10px", textAlign: "right", fontSize: 13, fontWeight: 600, color: "#111827", fontVariantNumeric: "tabular-nums" }}>
                    {fmt((ec.lignes || []).reduce((s, l) => s + (Number(l.credit) || 0), 0))}
                  </td>
                  <td />
                </tr>
              </tbody>
            </table>
          </section>
          );
        })}
      </div>
      {editingEcriture && (
        <EcritureEditModal
          ecriture={editingEcriture}
          onClose={() => setEditingEcriture(null)}
          onSaved={() => setEcrituresRefreshKey((k) => k + 1)}
          onValidated={() => setEcrituresRefreshKey((k) => k + 1)}
        />
      )}
    </>
  );
}
